/**
 * CotizacionPortalAdmin — panel (solo administradores) del acceso de una
 * cotización al Portal de Clientes: quién puede verla y responderla, solicitudes
 * de cambios del cliente, decisiones registradas y creación de nueva versión.
 *
 * Asignar es siempre una acción explícita sobre una cuenta del portal; nunca se
 * concede acceso por coincidencia de correo o RUT (se muestran solo como sugerencia).
 */
import { useState, useEffect, useCallback } from "react";
import { X, UserPlus, Ban, Check, Loader, AlertCircle, RefreshCw, GitBranch } from "lucide-react";
import {
  portalAdminCotizacion, asignarCotizacion, revocarAccesoCotizacion,
  marcarSolicitudAtendida, nuevaVersionCotizacion, ACCIONES_RESPUESTA, fmtCLP, mensajeErrorCotizacion,
} from "../lib/cotizacionesPortal";
import { fmtFechaHora } from "../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0", accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8", ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
};
const sec = { fontSize:12, fontWeight:700, color:C.sub, textTransform:"uppercase", letterSpacing:.4, margin:"18px 0 8px" };
const mini = (color) => ({ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 9px", borderRadius:6, fontSize:11, fontWeight:700,
  border:`1px solid ${color}50`, background:"transparent", color, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" });

export default function CotizacionPortalAdmin({ cotizacion, onCerrar, onCambio }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState("");
  const [aviso, setAviso] = useState(null);
  const [elegido, setElegido] = useState("");
  const [confirmarVersion, setConfirmarVersion] = useState(false);
  const [motivo, setMotivo] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try { setDatos(await portalAdminCotizacion(cotizacion.id)); }
    catch (e) { setAviso({ tipo:"error", texto: mensajeErrorCotizacion(e) }); }
    finally { setCargando(false); }
  }, [cotizacion.id]);
  useEffect(() => { cargar(); }, [cargar]);

  async function ejecutar(clave, fn, textoOk) {
    setOcupado(clave); setAviso(null);
    try { await fn(); setAviso({ tipo:"ok", texto: textoOk }); onCambio?.(); }
    catch (e) { setAviso({ tipo:"error", texto: mensajeErrorCotizacion(e) }); }
    finally { setOcupado(""); await cargar(); }
  }

  const candidato = datos?.candidatos?.find(c => c.user_id === elegido);
  const activos = (datos?.accesos || []).filter(a => a.estado === "activo");
  const solicitudes = (datos?.respuestas || []).filter(r => r.accion === "cambios" && !r.atendida_at);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="cpa-titulo"
      style={{position:"fixed", inset:0, zIndex:9997, background:"rgba(15,23,42,0.45)", display:"flex", alignItems:"flex-start",
        justifyContent:"center", padding:"24px 16px", overflowY:"auto"}}>
      <div style={{background:C.white, borderRadius:14, width:"100%", maxWidth:640, border:`1px solid ${C.border}`,
        boxShadow:"0 24px 80px #00000030", fontFamily:"'DM Sans',sans-serif", color:C.text}}>
        <div style={{padding:"16px 22px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <div>
            <h2 id="cpa-titulo" style={{margin:0, fontSize:16, fontWeight:700}}>Acceso al portal · {cotizacion.id}</h2>
            <div style={{fontSize:12, color:C.sub}}>{cotizacion.cliente} · {cotizacion.estado}{datos ? ` · versión ${datos.version}` : ""}</div>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" style={{background:"none", border:"none", cursor:"pointer", color:C.light}}><X size={18}/></button>
        </div>

        <div style={{padding:"4px 22px 22px"}}>
          {aviso && (
            <div role={aviso.tipo === "error" ? "alert" : "status"} style={{marginTop:14, padding:"9px 12px", borderRadius:8, fontSize:12, display:"flex", gap:7, alignItems:"center",
              color: aviso.tipo === "error" ? C.err : "#047857", background: aviso.tipo === "error" ? C.errL : C.okL}}>
              {aviso.tipo === "error" ? <AlertCircle size={13}/> : <Check size={13}/>} {aviso.texto}
            </div>
          )}
          {cargando && !datos ? <div style={{padding:24, textAlign:"center", color:C.sub}}><Loader size={14} className="spinning"/> Cargando…</div> : datos && (<>
            {solicitudes.length > 0 && (<>
              <div style={sec}>Solicitudes de cambios del cliente ({solicitudes.length})</div>
              {solicitudes.map(r => (
                <div key={r.id} style={{border:`1px solid #c7d2fe`, background:"#eef2ff", borderRadius:9, padding:"10px 12px", marginBottom:8, fontSize:13}}>
                  <div style={{fontSize:11, color:C.sub, marginBottom:3}}>{r.user_email} · {fmtFechaHora(r.created_at)} · versión {r.version}</div>
                  <div style={{whiteSpace:"pre-wrap"}}>{r.comentario}</div>
                  <button type="button" disabled={!!ocupado} style={{...mini("#6366f1"), marginTop:8}}
                    onClick={() => ejecutar("at" + r.id, () => marcarSolicitudAtendida(r.id), "Solicitud marcada como atendida.")}>
                    <Check size={11}/> Marcar atendida
                  </button>
                </div>
              ))}
            </>)}

            <div style={sec}>Clientes con acceso</div>
            {!datos.accesos.length ? <div style={{fontSize:13, color:C.sub}}>Nadie tiene acceso todavía. El cliente no verá esta cotización en su portal.</div> : (
              <div style={{display:"grid", gap:6}}>
                {datos.accesos.map(a => (
                  <div key={a.id} style={{display:"flex", justifyContent:"space-between", gap:10, alignItems:"center", flexWrap:"wrap",
                    border:`1px solid ${C.border}`, borderRadius:9, padding:"8px 12px", opacity: a.estado === "revocado" ? .6 : 1}}>
                    <div style={{fontSize:13}}>
                      <strong>{a.nombre}</strong> <span style={{color:C.sub}}>· {a.email}</span>
                      <div style={{fontSize:11, color:C.light}}>
                        {a.estado === "activo" ? `Asignado ${fmtFechaHora(a.asignado_at)}` : `Revocado ${fmtFechaHora(a.revocado_at)}`}
                      </div>
                    </div>
                    {a.estado === "activo" && (
                      <button type="button" disabled={!!ocupado} aria-label={`Quitar acceso de ${a.email}`} style={mini(C.err)}
                        onClick={() => ejecutar("rv" + a.id, () => revocarAccesoCotizacion(a.id, "Revocado desde el panel"), `Acceso de ${a.email} revocado.`)}>
                        <Ban size={11}/> Quitar acceso
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={sec}>Asignar a un cliente del portal</div>
            {!datos.candidatos.length ? (
              <div style={{fontSize:12, color:C.sub, lineHeight:1.5}}>
                No hay otras cuentas de cliente disponibles. Un cliente aparece aquí cuando aceptó una invitación a un proyecto
                o su contacto está vinculado a una cuenta del portal.
              </div>
            ) : (
              <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
                <select aria-label="Cliente del portal" value={elegido} onChange={e => setElegido(e.target.value)} disabled={!!ocupado}
                  style={{flex:"1 1 260px", padding:"8px 10px", borderRadius:7, border:`1px solid ${C.border}`, fontSize:13, fontFamily:"'DM Sans',sans-serif"}}>
                  <option value="">— Elegir cliente —</option>
                  {datos.candidatos.map(c => <option key={c.user_id} value={c.user_id}>{c.sugerido ? "★ " : ""}{c.nombre} · {c.email}</option>)}
                </select>
                <button type="button" disabled={!elegido || !!ocupado}
                  onClick={() => ejecutar("as", () => asignarCotizacion(cotizacion.id, elegido, candidato?.contacto_id || null), `Cotización asignada a ${candidato?.email}.`).then(() => setElegido(""))}
                  style={{display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:8, border:"none", fontWeight:700, fontSize:13,
                    fontFamily:"'DM Sans',sans-serif", background: elegido ? C.accent : C.light, color:"#fff", cursor: elegido ? "pointer" : "not-allowed"}}>
                  <UserPlus size={13}/> Asignar
                </button>
              </div>
            )}
            <div style={{fontSize:11, color:C.light, marginTop:6}}>★ coincide el correo o el RUT de la cotización: es solo una sugerencia, el acceso se concede únicamente al asignar.</div>
            {activos.length > 0 && cotizacion.estado === "Borrador" && (
              <div style={{fontSize:11, color:"#92400e", marginTop:6}}>La cotización está en Borrador: el cliente la verá cuando la envíes.</div>
            )}

            <div style={sec}>Decisiones registradas</div>
            {!datos.respuestas.length ? <div style={{fontSize:13, color:C.sub}}>Sin respuestas todavía.</div> : (
              <div style={{display:"grid", gap:6}}>
                {datos.respuestas.map(r => {
                  const a = ACCIONES_RESPUESTA[r.accion] || { label: r.accion, color: C.sub };
                  return (
                    <div key={r.id} style={{borderLeft:`3px solid ${a.color}`, paddingLeft:10, fontSize:12}}>
                      <strong style={{color:a.color}}>{a.label}</strong> · {fmtCLP(r.total)} · v{r.version} · {r.origen === "portal" ? "portal" : r.origen === "admin" ? "administración" : "enlace público"}
                      <span style={{color:C.light}}> · {r.user_email || "sin sesión"} · {fmtFechaHora(r.created_at)}</span>
                      {r.comentario && <div style={{color:C.sub, whiteSpace:"pre-wrap"}}>{r.comentario}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {datos.estado === "Aprobada" && (<>
              <div style={sec}>Modificar una cotización aprobada</div>
              {!confirmarVersion ? (
                <button type="button" disabled={!!ocupado} onClick={() => setConfirmarVersion(true)} style={mini(C.warn)}>
                  <GitBranch size={11}/> Crear nueva versión
                </button>
              ) : (
                <div style={{border:`1px solid ${C.warn}60`, background:C.warnL, borderRadius:9, padding:"10px 12px", fontSize:12, lineHeight:1.5}}>
                  La cotización vuelve a <strong>Borrador</strong> (el cliente deja de verla) y sube de versión. Al enviarla de nuevo,
                  el cliente deberá aprobarla otra vez; la aprobación actual queda guardada en el historial.
                  <input aria-label="Motivo de la nueva versión" value={motivo} onChange={e => setMotivo(e.target.value)} maxLength={300} placeholder="Motivo (opcional)"
                    style={{width:"100%", boxSizing:"border-box", margin:"8px 0", padding:"7px 10px", borderRadius:7, border:`1px solid ${C.border}`, fontSize:13}}/>
                  <div style={{display:"flex", gap:8}}>
                    <button type="button" disabled={!!ocupado} onClick={() => ejecutar("nv", () => nuevaVersionCotizacion(cotizacion.id, motivo), "Nueva versión creada: la cotización está en Borrador.").then(() => setConfirmarVersion(false))}
                      style={{...mini(C.warn), background:C.warn, color:"#fff"}}>Sí, crear nueva versión</button>
                    <button type="button" onClick={() => setConfirmarVersion(false)} style={mini(C.sub)}>Cancelar</button>
                  </div>
                </div>
              )}
            </>)}
          </>)}
          <div style={{display:"flex", justifyContent:"flex-end", marginTop:18}}>
            <button type="button" onClick={cargar} style={mini(C.sub)}><RefreshCw size={11} className={cargando ? "spinning" : ""}/> Actualizar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
